import {TextStyle} from 'react-native';

import config from '../../config';

import {resolveWeight} from './fontWeight';

/**
 * 12 of the 13 typography tokens specified in DIS-1736, from rubik.pdf and geologica.pdf.
 * The 13th, type.button.navigation, is width-dependent and lives in useNavigationTypeToken
 * (Task 3), not here.
 *
 * letterSpacing and lineHeight are deliberately absent. DIS-1737 is an open blocker:
 * neither is specified for any of the 13 styles. Emitting 0 would present a guess as a
 * signed-off value. See the guard test in __tests__/design-system/typography.test.ts.
 */

// The only brand-dependent weight in the set. Harry Brown ships Geologica's price at
// 700; the three Rubik brands ship it at 500. Keyed on BASE_TENANT rather than on the
// font family name, which would break the moment a second brand adopts Geologica.
const priceWeight = config.BASE_TENANT === 'HB' ? 'bold' : 'medium';

const freeze = <T extends object>(o: T): Readonly<T> => Object.freeze(o);

export const type = freeze({
  heading: freeze({
    h1: freeze({fontSize: 20, ...resolveWeight('bold')} as TextStyle),
    h2: freeze({fontSize: 16, ...resolveWeight('bold')} as TextStyle),
  }),
  paragraph: freeze({
    p1: freeze({fontSize: 18, ...resolveWeight('regular')} as TextStyle),
    p2: freeze({fontSize: 14, ...resolveWeight('regular')} as TextStyle),
    p3: freeze({fontSize: 12, ...resolveWeight('regular')} as TextStyle),
    p4: freeze({fontSize: 11, ...resolveWeight('regular')} as TextStyle),
  }),
  button: freeze({
    primary: freeze({fontSize: 14, ...resolveWeight('bold')} as TextStyle),
    secondary: freeze({fontSize: 14, ...resolveWeight('semiBold')} as TextStyle),
    tertiary: freeze({fontSize: 12, ...resolveWeight('semiBold')} as TextStyle),
    textLink: freeze({fontSize: 18, ...resolveWeight('regular')} as TextStyle),
    category: freeze({fontSize: 16, ...resolveWeight('regular')} as TextStyle),
  }),
  price: freeze({fontSize: 24, ...resolveWeight(priceWeight)} as TextStyle),
});

/**
 * OTP digits (DIS-1857). 22px is not one of the 13 DIS-1736 styles, so this
 * stays off `type` and the DIS-1737 guard. `resolveWeight('medium')` is what
 * makes Android draw the Medium face instead of Regular.
 */
export const otpDigit = freeze({fontSize: 22, ...resolveWeight('medium')} as TextStyle);
